// Inline SVG icons ported verbatim from the design (`DateField.dc` / `TimeField.dc`). Kept local
// so the field's visual identity matches the handoff exactly, independent of the shared icon set.

interface SvgIconProps {
  size?: number;
}

export function CalendarIcon({ size = 13 }: SvgIconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <rect x={3} y={4.5} width={18} height={16} rx={2} />
      <path d="M3 9h18" />
      <path d="M8 2.5v4" />
      <path d="M16 2.5v4" />
    </svg>
  );
}

export function ClockIcon({ size = 13 }: SvgIconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <circle cx={12} cy={12} r={9} />
      <path d="M12 7.5V12l3 2" />
    </svg>
  );
}

export function ChevronDownIcon({ size = 13 }: SvgIconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M6 9l6 6 6-6" />
    </svg>
  );
}

export function ChevronLeftIcon({ size = 13 }: SvgIconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M15 18l-6-6 6-6" />
    </svg>
  );
}

export function ChevronRightIcon({ size = 13 }: SvgIconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M9 18l6-6-6-6" />
    </svg>
  );
}

/** Double chevron — jumps a whole year in the calendar header. */
export function DoubleChevronIcon({ dir, size = 13 }: SvgIconProps & { dir: 'left' | 'right' }) {
  const paths =
    dir === 'left' ? ['M13 17l-5-5 5-5', 'M19 17l-5-5 5-5'] : ['M11 17l5-5-5-5', 'M5 17l5-5-5-5'];
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      {paths.map((d) => (
        <path key={d} d={d} />
      ))}
    </svg>
  );
}

export function TodayIcon({ size = 12 }: SvgIconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 12 12"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      aria-hidden
    >
      <circle cx={6} cy={6} r={3.2} />
      <line x1={6} y1={0.6} x2={6} y2={2.4} />
      <line x1={6} y1={9.6} x2={6} y2={11.4} />
      <line x1={0.6} y1={6} x2={2.4} y2={6} />
      <line x1={9.6} y1={6} x2={11.4} y2={6} />
    </svg>
  );
}

/** Small clock used as a caption glyph above a time grid. */
export function ClockMiniIcon({ size = 11 }: SvgIconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <circle cx={12} cy={12} r={9} />
      <path d="M12 7.5V12l3 2" />
    </svg>
  );
}
